import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";

// Stage 5 V2 Dashboard — replaces the legacy pipeline dashboard.
// Data source: `GET /admin/dashboard/v2` (projection-only endpoint
// that reuses the SAME authorities as the rest of the admin surface —
// A2 workstage/state, A3 activity, A5/A6 next-action/attention, V1
// first-contact authority).  NO independent lifecycle calculation,
// NO secondary Attention / Follow-up / Activity feed.  §4 rule.

const WORKSTAGE_NAMES = {
    1: "Initial Enquiry + Brief Development",
    2: "Project Feasibility",
    3: "Concept Design",
    4: "Developed + Technical Design",
    5: "Pre-Construction",
    6: "Construction",
    7: "Post-Construction",
};

function SectionTitle({ children, testId }) {
    return (
        <p className="font-manrope font-semibold text-terracotta text-[11px] tracking-[0.22em] uppercase mb-3"
           data-testid={testId}>
            {children}
        </p>
    );
}

function MetricCard({ value, label, hint, tooltip, testId }) {
    return (
        <div className="bg-white/70 border border-charcoal/10 rounded-lg p-5" data-testid={testId} title={tooltip || undefined}>
            <div className="font-manrope text-[11px] text-charcoal/60">{label}</div>
            <div className="font-manrope font-extrabold text-charcoal text-[34px] leading-none mt-2">{value ?? "—"}</div>
            {hint && <div className="mt-2 font-inter text-[11.5px] text-charcoal/50">{hint}</div>}
        </div>
    );
}

function WorkstageTile({ ws, count, name, onClick }) {
    return (
        <button onClick={onClick}
                className="text-left bg-white/70 border border-charcoal/10 rounded-md p-4 hover:border-terracotta transition-colors"
                data-testid={`dash-ws-${ws}`}>
            <div className="font-manrope font-extrabold text-charcoal text-[22px] leading-none">{count ?? 0}</div>
            <div className="mt-1 font-manrope text-[10px] uppercase tracking-[0.14em] text-charcoal/60">WS{ws}</div>
            <div className="mt-0.5 font-inter text-[11.5px] text-charcoal/70 leading-tight">{name}</div>
        </button>
    );
}

function fmtDueBadge(r) {
    if (r.overdue) return <span className="text-[11px] font-manrope font-bold tracking-wide text-terracotta uppercase">Overdue</span>;
    if (!r.due_at) return <span className="text-[11.5px] text-charcoal/40">—</span>;
    try {
        const d = new Date(r.due_at);
        const today = new Date();
        const same = d.toDateString() === today.toDateString();
        if (same) return <span className="text-[11px] font-manrope font-bold tracking-wide text-[#9a5a00] uppercase">Today</span>;
        return <span className="text-[12px] text-charcoal/70">{d.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}</span>;
    } catch { return <span className="text-[11.5px] text-charcoal/40">—</span>; }
}

function fmtRel(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diffH = (Date.now() - d.getTime()) / 36e5;
        if (diffH < 1) return `${Math.max(1, Math.floor(diffH * 60))} min ago`;
        if (diffH < 24) return `${Math.floor(diffH)}h ago`;
        if (diffH < 48) return "yesterday";
        return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
    } catch { return ""; }
}

export default function AdminDashboard() {
    usePageMeta({ title: "Dashboard | Studio G4 Admin" });
    const [data, setData] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/admin/dashboard/v2")
            .then(r => setData(r.data))
            .catch(() => setData({ today: {}, workstage: {}, portfolio: {}, priority: [], activity: [] }));
    }, [reloadKey]);

    if (!data) return <div className="opacity-60 font-inter" data-testid="admin-dashboard-loading">Loading dashboard…</div>;

    const today = data.today || {};
    const ws = data.workstage || {};
    const p = data.portfolio || {};
    const priority = data.priority || [];
    const activity = data.activity || [];
    const src = p.new_enquiries_source_split || {};

    return (
        <div className="space-y-10 max-w-[1500px]" data-testid="admin-dashboard">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="font-manrope font-bold text-charcoal text-[28px] md:text-[32px] mb-1">Dashboard</h1>
                    <p className="font-inter text-charcoal/60 text-[14px]">Stage 5 operational overview using the same authoritative project, workload and activity data as the rest of the admin portal.</p>
                </div>
                <button onClick={() => setReloadKey(k => k + 1)}
                        className="border border-charcoal/15 rounded-md px-3 py-2 text-[13px] font-manrope font-semibold hover:bg-white"
                        data-testid="dash-refresh">
                    Refresh
                </button>
            </div>

            <section>
                <SectionTitle testId="dash-section-today">Today / needs attention</SectionTitle>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard testId="dash-today-awaiting-first-contact"
                        label="New enquiries awaiting first contact"
                        value={today.new_enquiries_awaiting_first_contact}
                        tooltip="New enquiries where no email has yet been sent or phone call logged."
                        hint={today.oldest_wait_hours ? `Oldest waiting: ${today.oldest_wait_hours}h` : null} />
                    <MetricCard testId="dash-today-attention"
                        label="Unresolved attention"
                        value={today.unresolved_attention}
                        hint={today.projects_with_attention ? `Across ${today.projects_with_attention} project${today.projects_with_attention === 1 ? "" : "s"}` : null} />
                    <MetricCard testId="dash-today-overdue"
                        label="Overdue next actions"
                        value={today.overdue_next_actions} />
                    <MetricCard testId="dash-today-followups"
                        label="Follow-ups due"
                        value={today.follow_ups_due_today_or_overdue}
                        hint="Today and overdue" />
                </div>
            </section>

            <section>
                <SectionTitle testId="dash-section-workstage">Projects by Workstage</SectionTitle>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    {[1,2,3,4,5,6,7].map(n => (
                        <WorkstageTile key={n} ws={n}
                            count={ws[`ws${n}`]}
                            name={WORKSTAGE_NAMES[n]}
                            onClick={() => navigate(`/admin/projects`)} />
                    ))}
                </div>
            </section>

            <section>
                <SectionTitle testId="dash-section-portfolio">Portfolio</SectionTitle>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard testId="dash-portfolio-active" label="Active projects" value={p.active} />
                    <MetricCard testId="dash-portfolio-on-hold" label="On Hold" value={p.on_hold} />
                    <MetricCard testId="dash-portfolio-closed-mtd" label="Closed this month" value={p.closed_this_month} />
                    <MetricCard testId="dash-portfolio-new-enq-mtd"
                        label="New enquiries this month"
                        value={p.new_enquiries_this_month}
                        hint={`Website ${src.Website ?? 0} · Meta ${src.Meta ?? 0} · Manual ${src.Manual ?? 0}`} />
                </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-6">
                <section>
                    <SectionTitle testId="dash-section-priority">Priority workload</SectionTitle>
                    <div className="bg-white/70 border border-charcoal/10 rounded-lg overflow-hidden" data-testid="dash-priority-list">
                        <div className="grid grid-cols-[1.5fr_.85fr_.85fr_.7fr] gap-3 px-4 py-2.5 bg-[#faf9f7] text-[10px] uppercase tracking-wide text-charcoal/50 font-manrope font-semibold">
                            <div>Project</div><div>Owner</div><div>Next action</div><div>Due</div>
                        </div>
                        {priority.length === 0 && (
                            <div className="px-4 py-6 text-center text-charcoal/50 text-[13px] font-inter">Nothing needs priority attention right now.</div>
                        )}
                        {priority.map(r => (
                            <div key={r.id}
                                 onClick={() => navigate(`/admin/projects/${r.id}`)}
                                 className="grid grid-cols-[1.5fr_.85fr_.85fr_.7fr] gap-3 px-4 py-3 border-t border-charcoal/10 items-center cursor-pointer hover:bg-[#fdfbf8]"
                                 data-testid={`dash-priority-row-${r.id}`}>
                                <div className="min-w-0">
                                    <div className="font-manrope font-semibold text-[13.5px] truncate">{r.client_name || "—"}</div>
                                    <div className="font-inter text-[11.5px] text-charcoal/50 truncate">WS{r.workstage} · {r.workstage_name}</div>
                                </div>
                                <div className="text-[12.5px] text-charcoal/70 truncate">{r.owner_id || "—"}</div>
                                <div className="text-[12.5px] text-charcoal truncate">{r.next_action_title || r.attention || "—"}</div>
                                <div>{fmtDueBadge(r)}</div>
                            </div>
                        ))}
                    </div>
                </section>

                <section>
                    <SectionTitle testId="dash-section-activity">Recent meaningful activity</SectionTitle>
                    <div className="bg-white/70 border border-charcoal/10 rounded-lg p-4" data-testid="dash-activity-list">
                        {activity.length === 0 && (
                            <div className="py-4 text-center text-charcoal/50 text-[13px] font-inter">No recent activity yet.</div>
                        )}
                        {activity.map(a => (
                            <Link key={a.id || `${a.project_id}-${a.at}`}
                                  to={a.project_id ? `/admin/projects/${a.project_id}` : "#"}
                                  className="block py-3 border-t border-charcoal/10 first:border-t-0 hover:bg-[#fdfbf8] -mx-4 px-4"
                                  data-testid={`dash-activity-row`}>
                                <div className="font-manrope font-semibold text-[13.5px] text-charcoal">{a.title}</div>
                                <div className="font-inter text-[11.5px] text-charcoal/50 mt-0.5">
                                    {a.client_name ? `${a.client_name} · ` : ""}{fmtRel(a.at)}
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
